// app/main/flight-details/tabs/FlightHistoryItem.tsx
import React, { useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DateUtils from "app/utils/DateUtils";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: 14,
  },
  cardFooter: {
    opacity: 0.8,
  },
  diffOld: {
    textDecoration: "line-through",
    opacity: 0.8,
    wordBreak: "break-word",
  },
  diffNew: {
    fontWeight: 600,
    wordBreak: "break-word",
  },
  fieldChip: {
    marginRight: theme.spacing(1),
  },
}));

interface IProps {
  columns: string[];
  row: {
    id: string;
    flightId: string;
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    changedBy: string;
    changedAt: string; // ISO
  };
}

const prettifyField = (raw: string) =>
  (raw || "")
    .replace(/sector\[(\d+)\]/i, (_m, i) => `Sector ${Number(i) + 1}`)
    .replace(/\boneWay\b/i, "One Way")
    .replace(/\breturn\b/i, "Return")
    .replace(/[_\-\.]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .replace(/^./, (m) => m.toUpperCase());


const FlightHistoryItem: React.FC<IProps> = ({ columns, row }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [more, setMore] = useState(false);

  const changedAtHuman = row.changedAt
    ? DateUtils.toHumanDateWithHour(row.changedAt)
    : "-";

  return (
    <Card className={classes.root}>
      <CardContent>
        <Typography className={classes.title} color="textSecondary" gutterBottom>
          {t("app.main.schedules.history.changedOn", "Changed on")}:
        </Typography>
        <Typography variant="h5" component="h2">
          {changedAtHuman}
        </Typography>
        <br />
        <TableContainer component={Paper}>
          <Table aria-label="history table">
            <TableHead>
              <TableRow>
                {columns.map((col, idx) => (
                  <TableCell key={idx}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Chip
                    label={prettifyField(row.fieldName)}
                    size="small"
                    className={classes.fieldChip}
                  />
                </TableCell>
                <TableCell>
                  <span className={classes.diffOld}>{row.oldValue ?? "—"}</span>
                </TableCell>
                <TableCell>
                  <span className={classes.diffNew}>{row.newValue ?? "—"}</span>
                </TableCell>
                <TableCell>{row.changedBy || "—"}</TableCell>
                <TableCell>{changedAtHuman}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <CardActions>
        {more ? (
          <Typography variant="body2" component="p" className={classes.cardFooter}>
            {/* Room for extra metadata if backend provides it later, e.g. IP, reason */}
            {t("app.main.schedules.history.recordId", "Record ID")}:{" "}
            <strong>{row.id}</strong>
            <br />
            {t("app.main.schedules.history.flightId", "Flight ID")}:{" "}
            <strong>{row.flightId}</strong>
          </Typography>
        ) : (
          <Button onClick={() => setMore(!more)} size="small">
            {t("app.main.schedules.activities.reservationMore", "Read more")}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default FlightHistoryItem;
